import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { cn } from '@/lib/utils';
import { capitalizeName, getInitials } from '@/lib/utils/name';
import { Check, Copy, Info } from 'lucide-react';

type OfferStatusInfoBoxProps = {
  title: React.ReactNode;
  description: React.ReactNode;
  icon?: React.ReactNode;
  variant?: 'info' | 'warning';
  children?: React.ReactNode;
  className?: string;
};

export function OfferStatusInfoBox({
  title,
  description,
  icon,
  variant = 'info',
  children,
  className,
}: OfferStatusInfoBoxProps) {
  const variantClasses =
    variant === 'warning'
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : 'border-blue-100 bg-blue-50 text-blue-900';
  const descriptionClasses =
    variant === 'warning' ? 'text-amber-900' : 'text-blue-800';

  return (
    <div
      className={cn(
        'mt-4 flex items-center justify-between gap-6 rounded-lg border px-4 py-3 text-sm',
        variantClasses,
        className
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {icon}
        <div className="space-y-0.5">
          <p className="font-medium">{title}</p>
          <p className={descriptionClasses}>{description}</p>
        </div>
      </div>

      {children}
    </div>
  );
}

export function AutoEntrepreneurOfferInfoBox() {
  const { t } = useTranslation();
  const user = useUser();
  const { deal } = useDealContext();
  const brandDashboardUrl = deal?.brandId
    ? `https://mychoose.appchoose.io/${deal.brandId}/stock`
    : '';

  if (!user || !deal) return;

  return (
    <OfferStatusInfoBox
      icon={<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />}
      title={t('ui-v2.auto-entrepreneur-offer.title')}
      description={
        user.isInternalUser ? (
          <Trans
            components={{
              brand_dashboard: (
                <a
                  className="font-medium underline underline-offset-2"
                  href={brandDashboardUrl}
                  rel="noreferrer"
                  target="_blank"
                />
              ),
            }}
            i18nKey="ui-v2.auto-entrepreneur-offer.description-choose"
          />
        ) : (
          t('ui-v2.auto-entrepreneur-offer.description-brand')
        )
      }
    />
  );
}

export function NegotiationClosedBrandInfoBox() {
  const { t } = useTranslation();
  const user = useUser();
  const { deal } = useDealContext();

  if (!user || user.isInternalUser || !deal?.brandId) {
    return null;
  }

  return (
    <OfferStatusInfoBox
      icon={<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />}
      title={t('ui-v2.negotiation-closed-offer.title-brand')}
      description={t('ui-v2.negotiation-closed-offer.description-brand')}
    >
      <a
        className="shrink-0 rounded-md border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-900 transition-colors hover:bg-blue-100"
        href={`https://mychoose.appchoose.io/${deal.brandId}`}
        rel="noreferrer"
        target="_blank"
      >
        {t('ui-v2.negotiation-closed-offer.action-brand')}
      </a>
    </OfferStatusInfoBox>
  );
}

export function LockedOfferInfoBox() {
  const { t } = useTranslation();
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);
  const {
    deal: {
      hubspotInfos: { dealManager },
    },
  } = useDealContext();
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current !== null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  if (!isOfferLocked || !user) {
    return null;
  }

  const descriptionKey = user.isInternalUser
    ? 'ui-v2.locked-offer.description-choose'
    : 'ui-v2.locked-offer.description-brand';

  const managerInitials = getInitials(dealManager?.name);

  const handleCopyEmail = async (email: string | null | undefined) => {
    if (!email || !navigator.clipboard) return;
    await navigator.clipboard.writeText(email);

    if (copiedTimeoutRef.current !== null) {
      window.clearTimeout(copiedTimeoutRef.current);
    }

    setCopied(true);
    copiedTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      copiedTimeoutRef.current = null;
    }, 1800);
  };

  return (
    <OfferStatusInfoBox
      icon={<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />}
      title={
        user.isInternalUser
          ? t('ui-v2.locked-offer.title-choose')
          : t('ui-v2.locked-offer.title-brand')
      }
      description={t(descriptionKey, {
        dealManagerName:
          capitalizeName(dealManager?.name) ||
          t('ui-v2.locked-offer.placeholder-contact'),
      })}
      className={cn(!user.isInternalUser && 'py-1')}
    >
      {!user.isInternalUser && dealManager?.email && (
        <div className="my-1 flex shrink-0 flex-col items-center gap-1 border-l border-blue-100 pl-6 text-center">
          <Avatar className="h-11 w-11">
            <AvatarImage src={dealManager.photoUrl} alt={dealManager.name} />
            <AvatarFallback className="bg-blue-100 text-xs font-semibold text-blue-700">
              {managerInitials}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            onClick={() => void handleCopyEmail(dealManager.email)}
            className={cn(
              'inline-flex h-6 w-full items-center justify-center gap-2 rounded-md px-2 text-xs font-semibold leading-none transition-colors',
              copied
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-blue-900 hover:bg-blue-100'
            )}
          >
            <span>{dealManager.email}</span>
            {copied ? (
              <Check className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <Copy className="h-3.5 w-3.5 shrink-0" />
            )}
          </button>
        </div>
      )}
    </OfferStatusInfoBox>
  );
}
